// ═══════════════════════════════════════════════════════════════════════════
// Laravel backend calls — ported from the real TransfeFlow.tsx, verified
// against the actual payvit-admin controller (TransferController.php) so
// request shapes and validation bounds match production exactly.
// ═══════════════════════════════════════════════════════════════════════════
import type { Tx } from "../data"
import { STATUS } from "../data"

export const ADMIN_API = process.env.ADMIN_API as string

const STATUS_CACHE_KEY = "payvit-service-status"
const CACHE_TTL_MS = 5 * 60 * 1000

export interface ServiceStatus {
    deposits_enabled: boolean
    transfers_enabled: boolean
    taux_usd_htg: number
    taux_htg_usd: number
}

function saveStatusCache(s: ServiceStatus) {
    try {
        localStorage.setItem(STATUS_CACHE_KEY, JSON.stringify({ ...s, ts: Date.now() }))
    } catch (_e) {}
}
function loadStatusCache(): ServiceStatus | null {
    try {
        const raw = localStorage.getItem(STATUS_CACHE_KEY)
        if (!raw) return null
        const s = JSON.parse(raw)
        if (!s?.ts || Date.now() - s.ts > CACHE_TTL_MS) return null
        return s
    } catch (_e) {
        return null
    }
}

async function withTimeout<T>(fn: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> {
    const ctrl = new AbortController()
    const t = setTimeout(() => ctrl.abort(), ms)
    try {
        return await fn(ctrl.signal)
    } finally {
        clearTimeout(t)
    }
}

/** Tags every request as coming from payvit-web — the backend's
 *  DetectClientSurface middleware reads this to pick payvit-web's own
 *  team-set exchange rate (independent from the real website's) for both
 *  the quote a request asks for and the real settlement math a submission
 *  triggers. Absent entirely from the real production site's requests, so
 *  its behavior/rate is untouched by this change. */
const CLIENT_SURFACE_HEADERS: Record<string, string> = { "X-Payvit-Client": "webapp" }

export const bearerHeaders = (token: string): Record<string, string> => ({
    ...CLIENT_SURFACE_HEADERS,
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
})

export function loadCachedServiceStatus(): ServiceStatus | null {
    return loadStatusCache()
}

export async function fetchServiceStatus(): Promise<ServiceStatus | null> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/service-status`, { headers: CLIENT_SURFACE_HEADERS, signal })
            if (!r.ok) return null
            const s = (await r.json()) as ServiceStatus
            saveStatusCache(s)
            return s
        }, 10000)
    } catch (_e) {
        return null
    }
}

export interface VitBalance { count: number; progress: number; earned: number; spent: number }

/** Return shape is { usd, vit } — matches the sole caller's state shape
 *  (App.tsx's liveBalance) exactly. This used to return { balance, vit }
 *  while the caller read .usd, so every refresh (initial load AND every
 *  post-submission refresh) silently kept liveBalance.usd undefined and
 *  the UI fell back to the fixture balance forever — a real transfer's
 *  debit/credit landed correctly in the database but never appeared on
 *  screen, on any account, since day one of this being wired up. */
export async function fetchBalanceUsd(
    email: string,
    accessToken: string
): Promise<{ usd: number | null; vit: VitBalance | null }> {
    if (!email || !accessToken) return { usd: null, vit: null }
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/balance`, { headers: bearerHeaders(accessToken), signal })
            if (!r.ok) return { usd: null, vit: null }
            const data = await r.json()
            const usd = typeof data.balance_usd === "number" ? data.balance_usd : null
            const vit =
                data.vit && typeof data.vit.count === "number"
                    ? { count: data.vit.count, progress: data.vit.progress ?? 0, earned: data.vit.earned ?? 0, spent: data.vit.spent ?? 0 }
                    : null
            return { usd, vit }
        }, 15000)
    } catch (_e) {
        return { usd: null, vit: null }
    }
}

export interface PendingDeposit {
    id: string
    statut: string
    montant: number
    methode: string
    admin_note: string | null
    created_at: string
}

/** GET /client/pending-deposit — the latest en_attente/annulé depot, or null
 *  if there isn't one. Same jsonOrNull convention the backend uses for
 *  "nothing yet" (200 with a null body, not a 404). */
export async function fetchPendingDeposit(accessToken: string): Promise<PendingDeposit | null> {
    if (!accessToken) return null
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/pending-deposit`, { headers: bearerHeaders(accessToken), signal })
            if (!r.ok) return null
            const data = await r.json()
            return data && typeof data === "object" && data.id ? (data as PendingDeposit) : null
        }, 15000)
    } catch (_e) {
        return null
    }
}

// ── Unified transaction history ─────────────────────────────────────────────
// GET /client/history — one round trip across all six transaction tables.
// Each kind has its own statut vocabulary; mapHistoryToTx normalizes it into
// the demo's own STATUS keys so Row/Istorik/Akey need no changes at all.
interface HistoryRow {
    id: string | number
    kind: "depot" | "transfert" | "transfert_interne" | "retrait" | "bridge" | "remittance"
    nit: string
    statut: string
    created_at: string
    montant: number
    methode: string
    counterparty: string
    direction: "in" | "out"
    native_amount?: number
    native_currency?: string
    /** Real fee the sender paid, in USD — absent (not zero) for the
     *  receiving side of an internal transfer or a Payvit-received
     *  remittance, since only the payer actually paid one. */
    fee_usd?: number | null
}

/** Per-kind statut vocab (confirmed against Architecture.md / the real
 *  schema's CHECK constraints) collapsed into the demo's five-value STATUS
 *  set. `complété` is the only label common to every table, so anything
 *  unrecognized falls back to `en_attente` (never silently drawn as
 *  success). */
function mapStatut(kind: HistoryRow["kind"], statut: string): keyof typeof STATUS {
    const s = statut.toLowerCase()
    if (s === "complété" || s === "completed") return "complété"
    if (s === "rejete" || s === "rejected" || s === "annulé" || s === "échoué") return "rejete"
    if (s === "approuve" || s === "approved" || s === "debit_ok" || s === "credit_ok") return "approuve"
    if (kind === "depot" && (s === "en_attente" || s === "paye")) return "paye"
    return "en_attente"
}

function mapKind(row: HistoryRow): Tx["kind"] {
    if (row.kind === "depot") return "depo"
    if (row.kind === "retrait") return "retre"
    return "transfe"
}

const KREYOL_MONTHS = ["janvye", "fevriye", "mas", "avril", "me", "jen", "jiyè", "out", "septanm", "oktòb", "novanm", "desanm"]
const sameCalendarDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** The backend returns a raw ISO timestamp (`created_at`) — every other
 *  `Tx.at` in this app is a pre-formatted Kreyòl display string ("Jodi a ·
 *  14:22", "28 jiyè · 16:12"), so real history needs the same treatment
 *  here or every real transaction row would show a raw ISO string instead.
 *  Formats in the VIEWER's local time zone, deliberately — a timestamp is
 *  more useful read in the time it actually happened for the person
 *  looking at it, not the server's. */
function formatHistoryDate(iso: string): string {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const now = new Date()
    const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    if (sameCalendarDay(d, now)) return `Jodi a · ${time}`
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    if (sameCalendarDay(d, yesterday)) return `Yè · ${time}`
    return `${d.getDate()} ${KREYOL_MONTHS[d.getMonth()]} · ${time}`
}

export function mapHistoryToTx(rows: HistoryRow[]): Tx[] {
    return rows.map((r) => ({
        id: `hist-${r.kind}-${r.id}`,
        seed: `hist-${r.kind}-${r.id}`,
        kind: mapKind(r),
        name: r.counterparty || r.methode || "Tranzaksyon",
        handle: r.counterparty || "",
        method: r.native_currency ? `${r.methode} (${r.native_currency})` : r.methode,
        nit: r.nit,
        amount: r.montant,
        statut: mapStatut(r.kind, r.statut),
        at: formatHistoryDate(r.created_at),
        fee: r.fee_usd != null ? r.fee_usd : undefined,
    }))
}

export async function fetchHistory(accessToken: string): Promise<Tx[] | null> {
    if (!accessToken) return null
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/history`, { headers: bearerHeaders(accessToken), signal })
            if (!r.ok) return null
            const data = await r.json()
            const rows = Array.isArray(data.transactions) ? (data.transactions as HistoryRow[]) : []
            return mapHistoryToTx(rows)
        }, 15000)
    } catch (_e) {
        return null
    }
}

// ── Fee math — ported verbatim from TransfeFlow.tsx ─────────────────────────
export function solveFeeForNet(targetNetUsd: number, calcFee: (usd: number) => number): { feeUsd: number; totalUsd: number } {
    let feeUsd = calcFee(targetNetUsd)
    for (let i = 0; i < 12; i++) {
        const nextFee = calcFee(targetNetUsd + feeUsd)
        if (nextFee === feeUsd) break
        feeUsd = nextFee
    }
    return { feeUsd, totalUsd: targetNetUsd + feeUsd }
}

export function generateIdempotencyKey(): string {
    try {
        if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID()
    } catch (_e) {}
    return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

// ── Submission endpoints ─────────────────────────────────────────────────────
export interface SubmitResult {
    ok: boolean
    error?: string
    nit?: string
    duplicate?: boolean
}

async function postJson(path: string, body: any, accessToken: string, timeoutMs = 15000): Promise<SubmitResult> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}${path}`, {
                method: "POST",
                headers: bearerHeaders(accessToken),
                body: JSON.stringify(body),
                signal,
            })
            const data = await r.json().catch(() => ({}))
            if (!r.ok) {
                return { ok: false, error: data.error ?? data.message ?? "Erè. Eseye ankò." }
            }
            return { ok: true, nit: data.nit, duplicate: data.duplicate }
        }, timeoutMs)
    } catch (e: any) {
        if (e?.name === "AbortError") return { ok: false, error: "Demann lan pran twòp tan. Eseye ankò." }
        return { ok: false, error: "Pwoblèm koneksyon. Verifye entènèt ou epi eseye ankò." }
    }
}

export function submitWithdrawal(
    payload: {
        methode: "MonCash" | "NatCash"
        telephone: string
        montant_usd: number
        qr_code_image?: string
        idempotency_key?: string
        vit_spent_count?: number
        vit_mode?: "reduce" | "add"
    },
    accessToken: string
) {
    return postJson("/transfer/retrait", payload, accessToken)
}

export function submitRemittance(
    payload: {
        pay_provider: string
        receive_provider: string
        montant_pay: number
        pay_currency: "USD" | "HTG"
        receiver_name?: string
        receiver_handle?: string
        receiver_account_name?: string
        txn_id?: string
        proof_image?: string
        qr_code_image?: string
        client_note?: string
        receiver_payvit_email?: string
        idempotency_key?: string
        vit_spent_count?: number
        vit_mode?: "reduce" | "add"
    },
    accessToken: string
) {
    return postJson("/transfer/remittance", payload, accessToken)
}

export function submitBridgeTransfer(
    payload: {
        direction: "moncash_to_natcash" | "natcash_to_moncash"
        sender_phone: string
        receiver_phone: string
        montant_htg: number
        qr_code_image?: string
        proof_image?: string
        idempotency_key?: string
    },
    accessToken: string
) {
    return postJson("/transfer/bridge", payload, accessToken)
}

/** Dedicated, fee-free top-up endpoint — NOT /transfer/remittance. This is
 *  what Rechaje calls. Response also carries {compte, instructions,
 *  amount_htg, amount_usd, fee_usd, net_usd}, but SubmitResult only surfaces
 *  {ok, nit, error} — the rest isn't needed since Rechaje re-derives its own
 *  summary client-side before submit. */
export function submitDeposit(
    methode: "MonCash" | "NatCash",
    payload: { montant_htg: number; telephone?: string; txn_id?: string; proof_image?: string },
    accessToken: string
) {
    const path = methode === "MonCash" ? "/depot/moncash" : "/depot/natcash"
    return postJson(path, payload, accessToken)
}
 
/** Payvit-to-Payvit by email — POST /transfer/interne. No proof, no rail,
 *  no fee UI: it's a balance-to-balance move between two Payvit accounts,
 *  gated only by amount ($5-$200, matching every other tier-based route). */
export function submitInterne(
    payload: { receiver_email: string; montant_usd: number },
    accessToken: string
) {
    return postJson("/transfer/interne", payload, accessToken)
}

// ═══════════════════════════════════════════════════════════════════════════
// Virtual card (WaldenPay-backed) — verified against CardController.php +
// WaldenPayCardService.php directly, NOT the older Kripicard design
// described in this project's own architecture notes, which the backend
// has since replaced outright (same /client/card/* route surface, entirely
// different request/response shapes and lifecycle underneath). `details`/
// `transactions` are the one exception: those two pass WaldenPay's raw
// response straight through (`return $res['body']`), and the backend's own
// comments say the exact populated-card field names were never confirmed
// live. Rendered defensively below — no field name there is asserted with
// the same confidence as everything else in this file.
// ═══════════════════════════════════════════════════════════════════════════

export interface CardQuote {
    balance_usd: number
    fee_usd: number
    total_usd: number
    total_htg: number
    min_balance_usd: number
    account_moncash: string
    account_natcash: string
}

export async function fetchCardQuote(amountUsd: number, accessToken: string): Promise<CardQuote | null> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/card/quote?amount=${encodeURIComponent(String(amountUsd))}`, {
                headers: bearerHeaders(accessToken), signal,
            })
            if (!r.ok) return null
            return (await r.json()) as CardQuote
        }, 15000)
    } catch (_e) {
        return null
    }
}

export function submitCardCreate(
    payload: {
        card_name: string; requested_balance_usd: number; payment_method: "MonCash" | "NatCash"
        payer_phone?: string; txn_id?: string; proof_image: string; id_document_image: string
        declared_amount_htg: number; idempotency_key: string
    },
    accessToken: string
) {
    return postJson("/client/card/create", payload, accessToken, 20000)
}

export interface CardStatus {
    nit: string
    card_name: string
    status: "submitted" | "awaiting_deposit" | "active" | "frozen" | "rejected" | "refund_owed" | "refunded"
    requested_balance_usd: number
    last4: string | null
    admin_note: string | null
    deposit_address: string | null
    exact_amount: string | null
    network: string | null
    currency: string | null
    expires_at: string | null
    created_at: string
}

/** GET /client/card — jsonOrNull convention, same as pending-deposit: a
 *  200 with a null body means no card has ever been requested. */
export async function fetchCardStatus(accessToken: string): Promise<CardStatus | null> {
    if (!accessToken) return null
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/card`, { headers: bearerHeaders(accessToken), signal })
            if (!r.ok) return null
            const data = await r.json()
            return data && typeof data === "object" && data.nit ? (data as CardStatus) : null
        }, 15000)
    } catch (_e) {
        return null
    }
}

/** POST (not GET — the backend deliberately throttles this one harder,
 *  15/min, since it's the endpoint that can reveal a real card number).
 *  Raw WaldenPay body on success — field names not asserted, see file
 *  banner above. */
export async function fetchCardDetails(accessToken: string): Promise<{ ok: boolean; error?: string; raw?: any }> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/card/details`, {
                method: "POST", headers: bearerHeaders(accessToken), signal,
            })
            const data = await r.json().catch(() => ({}))
            if (!r.ok) return { ok: false, error: data.error ?? "Erè. Eseye ankò." }
            return { ok: true, raw: data }
        }, 15000)
    } catch (_e) {
        return { ok: false, error: "Pwoblèm koneksyon. Verifye entènèt ou epi eseye ankò." }
    }
}

export async function fetchCardTransactions(accessToken: string): Promise<{ ok: boolean; error?: string; raw?: any }> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/card/transactions`, { headers: bearerHeaders(accessToken), signal })
            const data = await r.json().catch(() => ({}))
            if (!r.ok) return { ok: false, error: data.error ?? "Erè. Eseye ankò." }
            return { ok: true, raw: data }
        }, 15000)
    } catch (_e) {
        return { ok: false, error: "Pwoblèm koneksyon. Verifye entènèt ou epi eseye ankò." }
    }
}

/** Always returns an error today — WaldenPay's reseller API has no
 *  documented freeze endpoint, and the backend returns a clear
 *  "not supported yet" message rather than silently no-oping. Wired for
 *  real (not faked as a working toggle, unlike the previous fixture UI)
 *  so the moment the backend adds support, this starts working with no
 *  frontend change needed. */
export function submitCardFreeze(action: "freeze" | "unfreeze", accessToken: string) {
    return postJson("/client/card/freeze", { action }, accessToken)
}

export interface CardTopupQuote {
    base_usd: number
    fee_usd: number
    total_usd: number
    total_htg: number
    account_moncash: string
    account_natcash: string
}

export async function fetchCardTopupQuote(amountUsd: number, accessToken: string): Promise<CardTopupQuote | null> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/card/topup/quote?amount=${encodeURIComponent(String(amountUsd))}`, {
                headers: bearerHeaders(accessToken), signal,
            })
            if (!r.ok) return null
            return (await r.json()) as CardTopupQuote
        }, 15000)
    } catch (_e) {
        return null
    }
}

export function submitCardTopup(
    payload: {
        base_amount_usd: number; payment_method: "MonCash" | "NatCash"; payer_phone?: string
        txn_id?: string; proof_image: string; declared_amount_htg: number; idempotency_key: string
    },
    accessToken: string
) {
    return postJson("/client/card/topup", payload, accessToken, 20000)
}

export interface CardTopupHistoryRow {
    nit: string
    base_amount_usd: number
    fee_usd: number
    status: string
    created_at: string
}

export async function fetchCardTopupHistory(accessToken: string): Promise<CardTopupHistoryRow[]> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/card/topup/history`, { headers: bearerHeaders(accessToken), signal })
            if (!r.ok) return []
            const data = await r.json()
            return Array.isArray(data) ? data : []
        }, 15000)
    } catch (_e) {
        return []
    }
}

// ── Saved recipients — a real per-account address book ─────────────────────
// Was localStorage-only before, scoped to one browser/origin rather than the
// actual Payvit account: a user logging in anywhere else (or on a fresh
// deploy origin) saw an empty list even though nothing was actually lost —
// it had simply never been saved anywhere the account could find it.
export interface RemoteRecipient {
    id: string
    provider: string
    label: string
    handle: string
}

/** Returns null (not []) on any failure — callers should fall back to the
 *  last-known local cache rather than treat a network blip as "you have no
 *  saved recipients", same convention fetchBalanceUsd/fetchHistory use. */
export async function fetchRecipients(accessToken: string): Promise<RemoteRecipient[] | null> {
    if (!accessToken) return null
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/recipients`, { headers: bearerHeaders(accessToken), signal })
            if (!r.ok) return null
            const data = await r.json()
            return Array.isArray(data.recipients) ? data.recipients : null
        }, 15000)
    } catch (_e) {
        return null
    }
}

export async function submitRecipient(
    payload: { provider: string; label: string; handle: string },
    accessToken: string
): Promise<{ ok: true; recipient: RemoteRecipient } | { ok: false; error: string }> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/recipients`, {
                method: "POST", headers: bearerHeaders(accessToken), body: JSON.stringify(payload), signal,
            })
            const data = await r.json().catch(() => ({}))
            if (!r.ok) return { ok: false, error: data.error ?? "Nou pa t kapab sove resipyan an." }
            return { ok: true, recipient: data as RemoteRecipient }
        }, 15000)
    } catch (e: any) {
        if (e?.name === "AbortError") return { ok: false, error: "Demann lan pran twòp tan. Eseye ankò." }
        return { ok: false, error: "Pwoblèm koneksyon. Verifye entènèt ou epi eseye ankò." }
    }
}

export async function deleteRecipient(id: string, accessToken: string): Promise<boolean> {
    try {
        return await withTimeout(async (signal) => {
            const r = await fetch(`${ADMIN_API}/client/recipients/${encodeURIComponent(id)}`, {
                method: "DELETE", headers: bearerHeaders(accessToken), signal,
            })
            return r.ok
        }, 15000)
    } catch (_e) {
        return false
    }
}
